import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { guestsService, type GuestPermission, type GuestScope } from '../services/guests'

interface ProjectPayload {
  scope_entity: 'project'
  permission: GuestPermission
  project: {
    id: number
    name: string
    description?: string | null
    status?: string | null
    start_date?: string | null
    due_date?: string | null
  }
}
interface TaskPayload {
  scope_entity: 'task'
  permission: GuestPermission
  task: {
    id: number
    title: string
    description?: string | null
    status?: string | null
    due_date?: string | null
    project_id?: number | null
  }
}

type Payload = ProjectPayload | TaskPayload

export default function GuestView() {
  const { token = '' } = useParams<{ token: string }>()
  const [payload, setPayload] = useState<Payload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [posting, setPosting] = useState(false)
  const [permission, setPermission] = useState<GuestPermission>('read')
  const [scope, setScope] = useState<GuestScope>('project')

  useEffect(() => {
    if (!token) return
    let cancelled = false
    guestsService
      .redeem(token)
      .then(r => {
        if (cancelled) return
        setPermission(r.permission)
        setScope(r.scope_entity)
      })
      .then(() => guestsService.view(token))
      .then(p => {
        if (!cancelled) setPayload(p as Payload)
      })
      .catch(() => {
        if (!cancelled) setError('This invite link is invalid, revoked, or expired.')
      })
    return () => {
      cancelled = true
    }
  }, [token])

  const submitComment = async () => {
    if (!draft.trim()) return
    setPosting(true)
    try {
      await guestsService.comment(token, draft.trim())
      setDraft('')
      toast.success('Comment posted')
    } catch {
      toast.error('Failed to post comment')
    } finally {
      setPosting(false)
    }
  }

  if (error) {
    return (
      <div className="mx-auto max-w-lg p-6">
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">Link invalid</h1>
        <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{error}</p>
      </div>
    )
  }
  if (!payload) {
    return <p className="p-6 text-sm text-[var(--color-text-secondary)]">Loading…</p>
  }

  const title =
    payload.scope_entity === 'project' ? payload.project.name : payload.task.title
  const description =
    payload.scope_entity === 'project' ? payload.project.description : payload.task.description

  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6">
      <div className="mb-4 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <p className="text-xs uppercase tracking-wide text-[var(--color-text-secondary)]">
          Guest view · {scope}
        </p>
        <h1 className="mt-1 text-xl font-semibold text-[var(--color-text-primary)] sm:text-2xl">
          {title}
        </h1>
        {description && (
          <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">
            {description}
          </p>
        )}
      </div>

      {permission === 'comment' && (
        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h2 className="mb-2 text-sm font-medium text-[var(--color-text-primary)]">
            Leave a comment
          </h2>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            rows={3}
            placeholder="Add a comment for the team…"
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-2 text-sm"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              disabled={posting || !draft.trim()}
              onClick={submitComment}
              className="min-h-[44px] rounded-md bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {posting ? 'Posting…' : 'Post comment'}
            </button>
          </div>
        </div>
      )}
      {permission === 'read' && (
        <p className="text-xs text-[var(--color-text-secondary)]">
          You have read-only access to this {scope}.
        </p>
      )}
    </div>
  )
}
